import React, { useState } from 'react';
import { getDataUrl } from '../utils/apiConfig';
import './ReportSection.css';
import ReportSectionImgCard from './ReportSectionImgCard';
import ReportSectionTableColumn from './ReportSectionTableColumn';
import VisibilityCaret from './VisibilityCaret';
import PublicationAnalysisWorkshopSection from './PublicationAnalysisWorkshopSection';

function ensurePeriod(str) {
  if (typeof str === 'string') {
      if (!str.endsWith('.')) {
          str += '.';
      }
      return str;
  } else {
      return '';
  }
}

const ReportSectionActivity2 = ({ selectedWorkshop, selectedWorkshopDetails }) => {

  let wsReports = selectedWorkshopDetails?.reports || {};
  const base = getDataUrl() + "/";

  let wsName = selectedWorkshop ? selectedWorkshop.name : "";
  let mostImportantSoilFunction = wsReports.max_overall_soil_function ? wsReports.max_overall_soil_function.toLowerCase() : "";

  const [isHidden, setIsHidden] = useState(true);
  const toggleVisibility = () => {
    setIsHidden(!isHidden);
  };

  return (
  <section>
    <div className="hero is-primary is-bold reportSectionHeader">
      <div id="tagline" className="hero-body">
        <div className="container">
          <div className="columns">            
            <div className="column is-full">
              <h1 className="title">
                Activity 2: Soil functions
                <VisibilityCaret onClick={toggleVisibility} isHidden={isHidden} />
              </h1>
              <h2 className="subtitle">
              Healthy soils support our lives every day through key soil functions: primary production, nutrient cycling, water regulation and purification, carbon and climate regulation, and habitat provision. The ability of soil to perform these functions underpins the productivity of our land management systems, ecosystem resilience, and the quality of water and air. Achieving and maintaining soil health is therefore essential for sustainable land stewardship, supporting the long-term viability and productivity of land as well as broader social and environmental well-being. Want to learn more about the science behind the soil function framework used by BENCHMARKS? Follow the <a href="https://edepot.wur.nl/564238" target="_blank" rel="noopener noreferrer">link</a>.
              </h2>
            </div>            
          </div>
        </div>
      </div>
    </div>    
    <div className={isHidden ? 'is-hidden' : 'container'}>
      <div className="columns">
        <div className="column is-6">          
          <ReportSectionImgCard
              title="Most important functions - by groups"
              imgSource={base + wsName + '/activity_2/soil_functions_by_group.png'}
              imgAltText="Bar chart of soil functions importance by  stakeholder group."
              text={`The results show that relevance of soil health functions was recognized by the workshop participants. Combining all stakeholders, soil function ${mostImportantSoilFunction} was considered most important. All functions were voted for, which highlights the interconnectedness and importance of fostering the soil functions. The difference with the average across all workshops, emphasizes that soil functioning is context-specific.`}
              stackVertical={true} />
        </div>
        <div className="column is-6">          
          <ReportSectionImgCard
              title="Most important functions - overall"
              imgSource={base + wsName + '/activity_2/soil_functions_distribution.png'}
              imgAltText=""
              text="The figure illustrates the variation in soil function prioritization between the stakeholder groups, with some functions receiving considerably more emphasis from certain stakeholders compared to others. Recognizing this diversity in priorities and expertise presents an opportunity to develop more comprehensive approaches to soil health management."
              stackVertical={true} />
        </div>
      </div>    
      <div className="block">
        <div className="text-by-image">
          <div className="columns">
            <div className="column is-12">
              <div className="content">
                <h2>Soil health objectives</h2>
                <div className="content-text">
                  {wsReports.text_activity_2_objectives}
                </div>
              </div>
            </div>
          </div>      
        </div>
      </div>
      <PublicationAnalysisWorkshopSection
        selectedWorkshop={selectedWorkshop}
        diagramKey="objectives"
      />
      <div className="block">
        <div className="text-by-image">
          <div className="columns">
            <div className="column is-12">
              <div className="content">
                <h2>From soil health to soil health objectives</h2>
                <div className="content-text">
                  This flowchart that outlines the connection from 'Soil Health' to 'Soil Health Objectives,' detailing the critical role of 'Soil Functions' and 'Soil Processes' in supporting ecosystem services. It emphasizes the necessity of defining specific soil health objectives to maintain or improve soil's ability to function effectively within context-specific systems.
                </div>
                <figure className="image">
                  <img
                    src={process.env.PUBLIC_URL + "/flowchart.png"}
                    alt=""
                  />
                </figure>
              </div>
            </div>
          </div>      
        </div>
      </div>
      <div className="block">
        <div className="columns text-by-image">
          <div className="column is-12">
            <div className="content">
              <h2>Current and future practices for soil health</h2>
              <p>This overview gives insight into the current and future soil management practices, activities and initiatives across different stakeholder groups.</p>
            </div>            
            <div className="columns is-vcentered">
              <div className="column is-1 has-text-centered">                
              </div>
              <div className="column  has-text-centered">
                <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/land_stewards.svg"} alt="Icon depicting land stewards."></img></span>
                <h3>LAND STEWARDS</h3>
              </div>
              <div className="column  has-text-centered">
                <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/private_actors.svg"} alt="Icon depicting private actors."></img></span>
                <h3>PRIVATE ACTORS</h3>
              </div>
              <div className="column  has-text-centered">
                <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/public_actors.svg"} alt="Icon depicting public actors."></img></span>
                <h3>PUBLIC ACTORS</h3>
              </div>
              <div className="column  has-text-centered">
                <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/knowledge_actors.svg"} alt="Icon depicting knowledge actors"></img></span>
                <h3>KNOWLEDGE ACTORS</h3>
              </div>
              <div className="column  has-text-centered">
                <span className="icon is-large"><img src={process.env.PUBLIC_URL + "/icons/citizens.svg"} alt="Icon depicting citizens."></img></span>
                <h3>CITIZENS</h3>
              </div>              
            </div>
            
            <div className="columns is-vcentered row-dark">
              <div className="column is-1 has-text-centered">
                <h3 style={{ transform: 'rotate(-90deg)' }}>CURRENT <br/> PRACTICES</h3>
              </div>              
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_current_landstewards)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_current_private)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_current_public)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_current_knowledge)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_current_citizens)}/>
            </div>
            <div className="columns is-vcentered row-light">
              <div className="column is-1 has-text-centered">
                <h3 style={{ transform: 'rotate(-90deg)' }}>FUTURE <br/> PRACTICES</h3>
              </div>              
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_future_landstewards)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_future_private)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_future_public)}/>
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_future_knowledge)}/>              
              <ReportSectionTableColumn text={ensurePeriod(wsReports.text_activity_2_future_citizens)}/>
            </div>
          </div>        
        </div>      
      </div>      
    </div>
  </section>
  );
};

export default ReportSectionActivity2;
